import { useEffect, useState } from 'react';
import { Pause, Play, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AppDrawer } from '@/components/AppDrawer';
import { m } from '@/lib/i18n';

/** A simple countdown for the chore at hand. */
export function TimerDrawer({ minutes, onClose }: { minutes: number | null; onClose: () => void }) {
  const total = (minutes ?? 0) * 60;
  const [left, setLeft] = useState(total);
  const [running, setRunning] = useState(true);
  useEffect(() => {
    setLeft(total);
    setRunning(true);
  }, [total]);
  useEffect(() => {
    if (!running || left <= 0) return;
    const id = setInterval(() => setLeft((l) => Math.max(0, l - 1)), 1000);
    return () => clearInterval(id);
  }, [running, left]);
  useEffect(() => {
    if (left === 0 && total > 0) navigator.vibrate?.([200, 100, 200]);
  }, [left, total]);
  const C = 2 * Math.PI * 80;
  return (
    <AppDrawer
      open={minutes !== null}
      onOpenChange={(o) => !o && onClose()}
      title={m.timer()}
      footer={
        <div className="grid grid-cols-2 gap-2">
          <Button size="lg" variant="secondary" onClick={() => setRunning((r) => !r)} disabled={left === 0}>
            {running ? <Pause /> : <Play />}
            {running ? m.timer_pause() : m.timer_resume()}
          </Button>
          <Button
            size="lg"
            variant="secondary"
            onClick={() => {
              setLeft(total);
              setRunning(true);
            }}
          >
            <RotateCcw /> {m.timer_restart()}
          </Button>
        </div>
      }
    >
      <div className="grid justify-items-center gap-3 py-2">
        <svg viewBox="0 0 180 180" className="size-48" role="timer" aria-live="off">
          <circle cx="90" cy="90" r="80" fill="none" strokeWidth="8" className="stroke-muted" />
          <circle
            cx="90"
            cy="90"
            r="80"
            fill="none"
            strokeWidth="8"
            strokeLinecap="round"
            className="stroke-primary origin-center -rotate-90 transition-[stroke-dashoffset] duration-1000 ease-linear"
            strokeDasharray={C}
            strokeDashoffset={total ? C * (1 - left / total) : 0}
          />
          <text x="90" y="102" textAnchor="middle" className="fill-foreground text-[34px] font-bold tabular-nums">
            {Math.floor(left / 60)}:{String(left % 60).padStart(2, '0')}
          </text>
        </svg>
        <p className="text-muted-foreground" aria-live="polite">
          {left === 0 ? m.times_up() : m.timer_go({ minutes: minutes ?? 0 })}
        </p>
      </div>
    </AppDrawer>
  );
}
